/**
 * Build NumberLineModel plots from inequality solution sets.
 *
 * Used by the writing-and-graphing-inequalities topic to render the correct
 * graph and plausible distractor graphs. Conversion is a pure function of the
 * solution set so the resulting plot is deterministic and testable.
 */

import type { Rational } from "../engine/rational";
import type { SolutionSet, Op } from "../engine/inequality";
import type { NumberLineModel } from "./plot-model";

/** A sensible integer window around the given boundary value(s). */
export function windowFor(values: readonly Rational[]): { min: number; max: number } {
  const nums = values.map((v) => v.toNumber());
  const lo = Math.min(...nums, 0);
  const hi = Math.max(...nums, 0);
  const pad = 3;
  return { min: Math.floor(lo) - pad, max: Math.ceil(hi) + pad };
}

/** A single-ray inequality (x op value) as a number-line plot. */
export function rayNumberLine(
  op: Op,
  value: Rational,
  win = windowFor([value]),
): NumberLineModel {
  const direction = op === "<" || op === "<=" ? "left" : "right";
  const closed = op === "<=" || op === ">=";
  return {
    kind: "number-line",
    min: win.min,
    max: win.max,
    points: [],
    rays: [{ from: value, direction, closed }],
  };
}

/** Convert a SolutionSet (ray) to a number-line model. Throws for non-rays. */
export function solutionSetNumberLine(set: SolutionSet): NumberLineModel {
  if (set.kind !== "ray") {
    throw new Error(`solutionSetNumberLine expects a ray, got ${set.kind}`);
  }
  return rayNumberLine(set.op, set.value);
}
